/**
 * Interface languages, as in ophelie: keys are the English sentences, so English needs no catalog
 * beyond plural singulars (`src/locales/en.json`; a missing key falls back to itself) and `fr`
 * carries the translations. Plurals use i18next's
 * suffixes on the English key: `t("{{count}} projects", { count })` reads "{{count}} projects_one"
 * and "{{count}} projects_other" (French counts 0 as "one").
 * Language: `?lng=`, then the last choice (localStorage `i18nextLng`), then the browser.
 */
import i18n, { type TFunction } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next, Trans, useTranslation } from "react-i18next";

// English only needs the singular of plural keys (the key itself is the plural).
import en from "../locales/en.json";

/** French catalog, one file per area of the app (`src/locales/fr/*.json`), merged. */
export const FR_CATALOGS = import.meta.glob<Record<string, string>>("../locales/fr/*.json", {
  eager: true,
  import: "default",
});
const fr: Record<string, string> = Object.assign({}, ...Object.values(FR_CATALOGS));

export const LANGUAGES = [
  { code: "fr", label: "Français", short: "FR" },
  { code: "en", label: "English", short: "EN" },
] as const;

export type Language = (typeof LANGUAGES)[number]["code"];

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { fr: { translation: fr }, en: { translation: en } },
    supportedLngs: LANGUAGES.map((l) => l.code),
    fallbackLng: false,
    keySeparator: false,
    nsSeparator: false,
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    detection: { order: ["querystring", "localStorage", "navigator"], caches: ["localStorage"] },
  });

function syncDocument(lng: string) {
  if (typeof document !== "undefined") document.documentElement.lang = lng;
}
syncDocument(i18n.resolvedLanguage ?? i18n.language);
i18n.on("languageChanged", syncDocument);

/** The active interface language (never undefined: an unsupported browser language reads as English). */
export function currentLanguage(): Language {
  return i18n.resolvedLanguage === "fr" ? "fr" : "en";
}

/** BCP 47 locale for Intl formatters. */
export function currentLocale(): string {
  return currentLanguage() === "fr" ? "fr-FR" : "en-GB";
}

/** Translate outside React (helpers, toasts). Inside components, prefer `useTranslation`. */
export const t: TFunction = ((...args: Parameters<TFunction>) => i18n.t(...args)) as TFunction;

/**
 * A record of English strings, each read in the current language at access time: module-level
 * constants (labels, referentials) stay plain objects for their consumers.
 */
export function translated<K extends PropertyKey>(labels: Record<K, string>): Record<K, string> {
  const out = {} as Record<K, string>;
  for (const key of Object.keys(labels) as K[]) {
    Object.defineProperty(out, key, { enumerable: true, get: () => t(labels[key]) });
  }
  return out;
}

export { i18n, Trans, useTranslation };
